// src/admin/pages/AdminBattleHistory.jsx
import React, { useState, useEffect } from "react";
import { battleHistoryService } from "../../../services/api";

const AdminBattleHistory = () => {
  const [battleHistory, setBattleHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formMode, setFormMode] = useState("add"); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    date: "",
    location: "",
    description: "",
  });
  const [currentId, setCurrentId] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchBattleHistory();
  }, []);

  const fetchBattleHistory = async () => {
    try {
      setLoading(true);
      const response = await battleHistoryService.getAll();
      setBattleHistory(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching battle history:", err);
      setError("Не вдалося завантажити бойову історію");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const resetForm = () => {
    setFormData({
      date: "",
      location: "",
      description: "",
    });
    setFormMode("add");
    setCurrentId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);

    try {
      if (formMode === "add") {
        await battleHistoryService.create(formData);
        setMessage({ type: "success", text: "Запис успішно додано!" });
      } else {
        await battleHistoryService.update(currentId, formData);
        setMessage({ type: "success", text: "Запис успішно оновлено!" });
      }

      resetForm();
      fetchBattleHistory();

      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error saving battle history:", err);
      setMessage({
        type: "error",
        text: `Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} запису`,
      });
    }
  };

  const handleEdit = (history) => {
    setFormData({
      date: history.date,
      location: history.location,
      description: history.description,
    });
    setCurrentId(history.orderID);
    setFormMode("edit");

    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цей запис?")) {
      return;
    }

    try {
      await battleHistoryService.delete(id);
      setBattleHistory(battleHistory.filter((item) => item.orderID !== id));
      setMessage({ type: "success", text: "Запис успішно видалено!" });

      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error deleting battle history:", err);
      setMessage({ type: "error", text: "Помилка при видаленні запису" });
    }
  };

  // New function to move item up in the list
  const moveItemUp = async (index) => {
    if (index <= 0) return; // Can't move up if already at the top

    try {
      // Create a new array to avoid mutating state directly
      const updatedHistory = [...battleHistory];

      // Get the items to swap
      const itemToMove = updatedHistory[index];
      const itemAbove = updatedHistory[index - 1];

      // Keep the original orderIDs
      const itemToMoveId = itemToMove.orderID;
      const itemAboveId = itemAbove.orderID;

      // Create new objects with swapped orderIDs
      const updatedItemToMove = { ...itemToMove, orderID: itemAboveId };
      const updatedItemAbove = { ...itemAbove, orderID: itemToMoveId };

      // Update the array
      updatedHistory[index] = updatedItemAbove;
      updatedHistory[index - 1] = updatedItemToMove;

      // Update state immediately for better UX
      setBattleHistory(updatedHistory);

      // Update on the server
      await Promise.all([
        battleHistoryService.update(itemAboveId, updatedItemToMove),
        battleHistoryService.update(itemToMoveId, updatedItemAbove),
      ]);

      setMessage({ type: "success", text: "Порядок успішно змінено!" });

      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error moving item:", err);
      setMessage({ type: "error", text: "Помилка при зміні порядку" });

      // Refresh the list to get the current state from server
      fetchBattleHistory();
    }
  };

  // New function to move item down in the list
  const moveItemDown = async (index) => {
    if (index >= battleHistory.length - 1) return; // Can't move down if already at the bottom

    try {
      // Create a new array to avoid mutating state directly
      const updatedHistory = [...battleHistory];

      // Get the items to swap
      const itemToMove = updatedHistory[index];
      const itemBelow = updatedHistory[index + 1];

      // Keep the original orderIDs
      const itemToMoveId = itemToMove.orderID;
      const itemBelowId = itemBelow.orderID;

      // Create new objects with swapped orderIDs
      const updatedItemToMove = { ...itemToMove, orderID: itemBelowId };
      const updatedItemBelow = { ...itemBelow, orderID: itemToMoveId };

      // Update the array
      updatedHistory[index] = updatedItemBelow;
      updatedHistory[index + 1] = updatedItemToMove;

      // Update state immediately for better UX
      setBattleHistory(updatedHistory);

      // Update on the server
      await Promise.all([
        battleHistoryService.update(itemBelowId, updatedItemToMove),
        battleHistoryService.update(itemToMoveId, updatedItemBelow),
      ]);

      setMessage({ type: "success", text: "Порядок успішно змінено!" });

      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error moving item:", err);
      setMessage({ type: "error", text: "Помилка при зміні порядку" });

      // Refresh the list to get the current state from server
      fetchBattleHistory();
    }
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Бойова історія</h1>
        <div style={styles.loadingContainer}>
          <p>Завантаження...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Бойова історія</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Бойова історія</h1>

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor:
              message.type === "success"
                ? "#d4edda"
                : message.type === "error"
                ? "#f8d7da"
                : message.type === "info"
                ? "#cce5ff"
                : "#fff3cd",
            color:
              message.type === "success"
                ? "#155724"
                : message.type === "error"
                ? "#721c24"
                : message.type === "info"
                ? "#004085"
                : "#856404",
          }}
        >
          {message.text}
        </div>
      )}

      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {formMode === "add" ? "Додати новий запис" : "Редагувати запис"}
        </h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="date">
              Дата:
            </label>
            <input
              style={styles.input}
              type="text"
              id="date"
              name="date"
              placeholder="Наприклад: Квітень 2022"
              value={formData.date}
              onChange={handleInputChange}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="location">
              Місце:
            </label>
            <input
              style={styles.input}
              type="text"
              id="location"
              name="location"
              placeholder="Наприклад: Харківська область"
              value={formData.location}
              onChange={handleInputChange}
              required
            />
          </div>

          <div style={styles.formGroupFullWidth}>
            <label style={styles.label} htmlFor="description">
              Опис подій:
            </label>
            <textarea
              style={styles.textArea}
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
            />
          </div>

          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>
                Скасувати
              </button>
            )}
            <button type="submit" style={styles.button}>
              {formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Порядок</th>
              <th style={styles.tableHeader}>Дата</th>
              <th style={styles.tableHeader}>Місце</th>
              <th style={styles.tableHeader}>Опис</th>
              <th style={styles.tableHeader}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {battleHistory.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  Немає доданих записів
                </td>
              </tr>
            ) : (
              battleHistory.map((item, index) => (
                <tr key={item.orderID} style={styles.tableRow}>
                  <td style={styles.tableCell} data-label="Позиція">
                    <div style={styles.positionButtonsContainer}>
                      <button
                        style={styles.positionButton}
                        onClick={() => moveItemUp(index)}
                        disabled={index === 0}
                        title="Перемістити вгору"
                      >
                        ▲
                      </button>
                      <button
                        style={styles.positionButton}
                        onClick={() => moveItemDown(index)}
                        disabled={index === battleHistory.length - 1}
                        title="Перемістити вниз"
                      >
                        ▼
                      </button>
                    </div>
                  </td>
                  <td style={styles.tableCell} data-label="Дата">
                    {item.date}
                  </td>
                  <td style={styles.tableCell} data-label="Місце">
                    {item.location}
                  </td>
                  <td style={styles.tableCell} data-label="Опис">
                    {item.description}
                  </td>

                  <td style={styles.tableCell} data-label="Дії">
                    <a style={styles.actionLink} onClick={() => handleEdit(item)}>
                      Редагувати
                    </a>
                    <a style={styles.actionLinkDanger} onClick={() => handleDelete(item.orderID)}>
                      Видалити
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  formGroupFullWidth: {
    display: "flex",
    flexDirection: "column",
    gridColumn: "1 / -1",
  },
  label: {
    marginBottom: "0.5rem",
    fontWeight: 600,
  },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  textArea: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    minHeight: "100px",
    resize: "vertical",
    outline: "none",
  },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#FFBB00",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gridColumn: "1 / -1",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    padding: "0.8rem",
    textAlign: "left",
    borderBottom: "1px solid #eee",
    backgroundColor: "#e8e8e8",
    fontWeight: 600,
  },
  tableRow: {
    borderBottom: "1px solid #eee",
  },
  tableCell: {
    padding: "0.8rem",
    textAlign: "left",
    borderBottom: "1px solid #eee",
  },
  actionLink: {
    color: "var(--primary-color)",
    textDecoration: "none",
    cursor: "pointer",
    marginRight: "1rem",
  },
  actionLinkDanger: {
    color: "var(--error-color)",
    textDecoration: "none",
    cursor: "pointer",
    marginRight: "1rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  message: {
    padding: "1rem",
    marginBottom: "1rem",
    borderRadius: "4px",
  },
  // New styles for position buttons
  positionButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  positionButton: {
    width: "30px",
    height: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f0f0f0",
    border: "1px solid #ddd",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    padding: 0,
    lineHeight: 1,
  },
};

export default AdminBattleHistory;
